import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import { BasePriceAudit } from '@app/api/price-service';
import { PriceListService } from './services/price-list.service';

interface HistoryTab {
  code: string;
  labelKey: string;
  priceType?: string;
  priceBookCode?: string;
}

const HISTORY_TABS: HistoryTab[] = [
  { code: 'COST', labelKey: 'price_list.tabs.cost', priceType: 'COST', priceBookCode: 'COST_DEFAULT' },
  { code: 'CLEARANCE', labelKey: 'price_list.tabs.clearance', priceType: 'CLEARANCE' },
  { code: 'RETAIL', labelKey: 'price_list.tabs.retail', priceType: 'BASE', priceBookCode: 'RETAIL_DEFAULT' },
  { code: 'WEB', labelKey: 'price_list.tabs.web', priceBookCode: 'WEB' },
  { code: 'POS', labelKey: 'price_list.tabs.pos', priceBookCode: 'POS' },
  {
    code: 'MARKETPLACE',
    labelKey: 'price_list.tabs.marketplace',
    priceType: 'CHANNEL_OVERRIDE',
    priceBookCode: 'MARKETPLACE',
  },
  { code: 'WHOLESALE', labelKey: 'price_list.tabs.wholesale', priceType: 'WHOLESALE', priceBookCode: 'WHOLESALE' },
  { code: 'DISTRIBUTION', labelKey: 'price_list.tabs.distribution', priceType: 'DISTRIBUTION', priceBookCode: 'DISTRIBUTION' },
  { code: 'FLASH_SALE', labelKey: 'price_list.tabs.flash_sale', priceType: 'FLASH_SALE' },
  { code: 'MARKET', labelKey: 'price_list.tabs.market', priceType: 'MARKET' },
];

@Component({
  selector: 'app-price-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    DatePickerModule,
    PaginatorModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './price-history.component.html',
  styleUrl: './price-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceHistoryComponent implements OnInit {
  private readonly priceListService = inject(PriceListService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);

  readonly tabs = HISTORY_TABS;
  readonly activeCode = signal(HISTORY_TABS[2].code);
  readonly activeTab = computed(
    () => this.tabs.find((tab) => tab.code === this.activeCode()) ?? this.tabs[0],
  );
  readonly items = signal<BasePriceAudit[]>([]);
  readonly loading = signal(false);
  readonly exporting = signal(false);
  readonly error = signal<string | null>(null);
  readonly page = signal(0);
  readonly pageSize = signal(20);
  readonly rowsPerPageOptions = [20, 50, 100];
  readonly goToPageNumber = signal(1);
  readonly total = signal(0);
  readonly maxPage = computed(() =>
    Math.max(1, Math.ceil(this.total() / this.pageSize()) || 1),
  );

  readonly form = this.formBuilder.group({
    skuName: this.formBuilder.nonNullable.control(''),
    skuCode: this.formBuilder.nonNullable.control(''),
    actor: this.formBuilder.nonNullable.control(''),
    startDate: this.formBuilder.control<Date | null>(null),
    endDate: this.formBuilder.control<Date | null>(null),
  });

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(0);
        this.goToPageNumber.set(1);
        this.loadChanges();
      });
    this.loadChanges();
  }

  selectTab(code: string): void {
    if (this.activeCode() === code) {
      return;
    }
    this.activeCode.set(code);
    this.page.set(0);
    this.goToPageNumber.set(1);
    this.loadChanges();
  }

  onTabKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    event.preventDefault();
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % this.tabs.length
        : (index - 1 + this.tabs.length) % this.tabs.length;
    this.selectTab(this.tabs[next].code);
  }

  clearSearch(): void {
    this.form.reset({
      skuName: '',
      skuCode: '',
      actor: '',
      startDate: null,
      endDate: null,
    });
  }

  exportExcel(): void {
    const value = this.form.getRawValue();
    const tab = this.activeTab();
    this.exporting.set(true);
    this.priceListService
      .exportPriceChanges({
        skuName: value.skuName.trim() || undefined,
        skuCode: value.skuCode.trim() || undefined,
        actor: value.actor.trim() || undefined,
        priceBookCode: tab.priceBookCode,
        priceType: tab.priceType,
        startDate: this.toFilterDate(value.startDate),
        endDate: this.toFilterDate(value.endDate),
        format: 'xlsx',
      })
      .subscribe({
        next: (blob) => {
          this.exporting.set(false);
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = 'price-change-audit.xlsx';
          anchor.click();
          URL.revokeObjectURL(url);
          this.messages.add({
            severity: 'success',
            summary: this.translate.instant('price_history.export_excel'),
          });
        },
        error: () => {
          this.exporting.set(false);
          this.messages.add({
            severity: 'error',
            summary: this.translate.instant('price_history.errors.export'),
          });
        },
      });
  }

  onTablePage(event: PaginatorState): void {
    const rows = event.rows ?? this.pageSize();
    const first = event.first ?? 0;
    this.pageSize.set(rows);
    this.page.set(Math.floor(first / rows));
    this.goToPageNumber.set(this.page() + 1);
    this.loadChanges();
  }

  onGoToPage(): void {
    const target = Math.min(Math.max(1, Number(this.goToPageNumber()) || 1), this.maxPage());
    this.goToPageNumber.set(target);
    this.page.set(target - 1);
    this.loadChanges();
  }

  reasonLabel(code?: string | null): string {
    if (!code) {
      return '—';
    }
    return `price_list.reasons.${code}`;
  }

  private toFilterDate(value: Date | null | undefined): string | undefined {
    if (!value) {
      return undefined;
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private loadChanges(): void {
    const value = this.form.getRawValue();
    const tab = this.activeTab();
    this.loading.set(true);
    this.error.set(null);
    this.priceListService
      .pagePriceChanges({
        page: this.page(),
        size: this.pageSize(),
        skuName: value.skuName.trim() || undefined,
        skuCode: value.skuCode.trim() || undefined,
        actor: value.actor.trim() || undefined,
        priceBookCode: tab.priceBookCode,
        priceType: tab.priceType,
        startDate: this.toFilterDate(value.startDate),
        endDate: this.toFilterDate(value.endDate),
      })
      .subscribe({
        next: (result) => {
          this.items.set(result?.pageContent ?? []);
          this.total.set(result?.totalElements ?? 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set('price_history.errors.load');
        },
      });
  }
}
